import { formatNumber } from 'utils/number';
import {
  DATE_FIELD_LABELS,
  DATE_ORDER,
  WELL_STATUS_LABELS,
  type WellStatusToken,
  type WorkspaceCompareSection,
} from 'types/well-workspace';
import { fieldDate } from 'components/well-cards';

/** One row of the mock's table: a well, a field, and the two sides. */
export interface CompareTableRow {
  id: string;
  uwi: string;
  well: string;
  label: string;
  from: string;
  to: string;
  /** The backend's row status — `changed`, `new`, or a future `removed`. */
  status: string;
  /** Create for a new record, edit for a change to one that exists. */
  action: 'create' | 'edit';
  /** Promoted once the change has reached the wells; null when the backend did not say. */
  promoted: 'promoted' | 'staged' | null;
  period: string | null;
}

/** One dataset section, rendered as a collapsible table. */
export interface CompareTableSection {
  key: string;
  label: string;
  rows: CompareTableRow[];
}

const EMPTY = '—';

const FIELD_LABELS: Record<string, string> = {
  ...DATE_FIELD_LABELS,
  status: 'SRP Well Status',
  currentOperator: 'Current Operator',
  holeDirection: 'Hole Direction',
  srpLandingZone: 'SIM Landing Zone',
  vendorLandingZone: 'Vendor Landing Zone',
  measuredDepthFeet: 'Vendor Measured Depth',
  trueVerticalDepthFeet: 'Vendor True Vertical Depth',
  leaseName: 'Lease Name',
  wellNumber: 'Well Number',
  // The Wellbore Geometry section: what a Well Workspace did to a bore's geometry.
  geometrySource: 'Geometry Source',
  controlPoints: 'Control Points',
  datumElevationFt: 'Datum Elevation',
  targetZone: 'Target Zone',
  // The Landing Interpretation section (INS-12936 UAT item 5): the landing analysis a
  // bore's interval was read from → the landing interval chosen.
  landingInterval: 'Landing Interval',
  // Confirming the system's interval over another workspace's manual pick removes it.
  manualLandingPick: 'Manual Landing Pick',
};

// The backend names diff fields in snake_case; every display map here (and the rest of
// the frontend) is camelCase. Normalizing at this boundary is what keeps a multi-word
// field like `current_operator` from silently missing its label and its card.
const toCamel = (field: string): string =>
  field.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());

// A readable fallback for a field the label map does not name. The backend's sections
// reach beyond the two well cards, so an unmapped field has to read as words rather
// than as `oil_volume_bbl`.
const humanize = (field: string): string =>
  field
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Depths arrive as bare numeric strings in the diff, so Compare has to format them the
// way the cards do — a five-figure depth is the difference between "21,345" and a number
// nobody can read at a glance.
const NUMERIC_FIELDS = new Set([
  'measuredDepthFeet',
  'trueVerticalDepthFeet',
  'datumElevationFt',
  'controlPoints',
]);
const DATE_FIELDS = new Set<string>(DATE_ORDER);

// How one side of a row reads: a status as its label, dates and depths as the cards
// show them, everything else as itself, so a value does not change shape just because
// Compare is open. The backend normalizes them to strings.
// A PostGIS value as it arrives on the wire: WKB hex, e.g.
// `0101000020E6100000D3B262769EA759C033EDEE43D6BA4040`. Long, unreadable, and on a
// geometry-heavy ingest it is most of the table.
const WKB_HEX = /^[0-9A-F]{40,}$/;

// WKB opens with a byte order, a type and an SRID — 18 hex characters that are the same
// for every point in the dataset. The coordinates follow, little-endian, so it is the
// bytes just after that header which differ between two nearby geometries. Slicing the
// tail instead would show the high-order bytes, identical for wells in the same field,
// and two genuinely different geometries would render as the same string — which in a
// diff reads as "nothing changed".
const WKB_HEADER_CHARS = 18;

/**
 * What a geometry cell shows instead of its blob: that it is a geometry, plus enough of
 * the varying bytes to tell two apart. Coordinates a reader can actually place are
 * Phase 2 enrichment; this stops the blob blowing the column out in R1.
 * @param {string} hex The WKB hex string.
 * @return {string} The shortened label.
 */
const geometryLabel = (hex: string): string =>
  `Geometry · ${hex
    .slice(WKB_HEADER_CHARS, WKB_HEADER_CHARS + 8)
    .toLowerCase()}`;

const display = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'string' && WKB_HEX.test(value)) {
    return geometryLabel(value);
  }
  if (field === 'status') {
    return WELL_STATUS_LABELS[value as WellStatusToken] ?? String(value);
  }
  if (DATE_FIELDS.has(field)) {
    return fieldDate(String(value));
  }
  if (NUMERIC_FIELDS.has(field)) {
    return formatNumber({
      value: String(value),
      minPrecision: 0,
      includeCommas: true,
      fallback: EMPTY,
    });
  }
  return String(value);
};

/**
 * The backend's sections, kept section-first and flattened into table rows.
 *
 * The mock reads a table per dataset with the well as a column, which is the shape the
 * endpoint already sends, so this only formats the two sides and keeps the section
 * order. Rows are dropped, never sections, so an emptied search still names the section.
 * @param {WorkspaceCompareSection[]} sections The compare endpoint's sections.
 * @param {string} query Free text matched against well name and UWI.
 * @return {CompareTableSection[]} Sections carrying at least one matching row.
 */
export function tableSections(
  sections: WorkspaceCompareSection[],
  query = ''
): CompareTableSection[] {
  const needle = query.trim().toLowerCase();
  const matches = (row: { well: string; uwi: string }) =>
    !needle ||
    (row.well || '').toLowerCase().includes(needle) ||
    row.uwi.toLowerCase().includes(needle);

  return sections
    .map((section) => ({
      key: section.key,
      label: section.label,
      rows: section.rows.filter(matches).map((row, index) => {
        const field = toCamel(row.field);

        return {
          // A production section repeats a field per period, so neither the field nor
          // the well is unique on its own.
          id: `${section.key}-${row.uwi}-${row.field}-${row.period ?? index}`,
          uwi: row.uwi,
          well: row.well || row.uwi,
          label: FIELD_LABELS[field] ?? humanize(row.field),
          from: display(field, row.from),
          to: display(field, row.to),
          status: row.status,
          // Older payloads carry only `status`; a new record is the only create.
          action:
            row.action ??
            (row.status === 'new' || row.status === 'added'
              ? 'create'
              : 'edit'),
          promoted: row.promoted ?? null,
          period: row.period,
        };
      }),
    }))
    .filter((section) => section.rows.length > 0);
}
